import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Settings, Users, ShoppingBag, Calendar, MessageSquare, TrendingUp, 
  Plus, Edit, Trash2, Save, X, AlertCircle, CheckCircle, Star
} from 'lucide-react';
import axios from 'axios';

const AdminPanel = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    orders: 0,
    submissions: 0,
    players: 0,
    products: 0,
    fixtures: 0,
    sponsors: 0
  });
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  // Data states
  const [products, setProducts] = useState([]);
  const [players, setPlayers] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [news, setNews] = useState([]);
  const [settings, setSettingsData] = useState({});
  
  // Player management states
  const [playerStats, setPlayerStats] = useState({});
  const [playerPayments, setPlayerPayments] = useState({});
  const [playerFeedback, setPlayerFeedback] = useState({});
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  
  // Database initialization state
  const [databaseStatus, setDatabaseStatus] = useState({ needs_initialization: false });

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchAllData();
    checkDatabaseStatus();
  }, []);

  const checkDatabaseStatus = async () => {
    try {
      const response = await axios.get('/admin/database-status');
      setDatabaseStatus(response.data);
    } catch (error) {
      console.error('Error checking database status:', error);
    }
  };

  const initializeDatabase = async () => {
    try {
      setLoading(true);
      await axios.post('/admin/initialize-database');
      showNotification('Database initialized successfully!', 'success');
      checkDatabaseStatus();
      fetchAllData();
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Error initializing database', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchAllData = async () => {
    try {
      const [
        productsRes, playersRes, fixturesRes, ordersRes, 
        sponsorsRes, submissionsRes, newsRes, settingsRes
      ] = await Promise.all([
        axios.get('/products').catch(() => ({ data: [] })),
        axios.get('/players').catch(() => ({ data: [] })),
        axios.get('/fixtures').catch(() => ({ data: [] })),
        axios.get('/orders').catch(() => ({ data: [] })),
        axios.get('/sponsors').catch(() => ({ data: [] })),
        axios.get('/join-us').catch(() => ({ data: [] })),
        axios.get('/admin/news').catch(() => ({ data: [] })),
        axios.get('/settings').catch(() => ({ data: {} }))
      ]);
      
      setProducts(productsRes.data);
      setPlayers(playersRes.data);
      setFixtures(fixturesRes.data);
      setOrders(ordersRes.data);
      setSponsors(sponsorsRes.data);
      setSubmissions(submissionsRes.data);
      setNews(newsRes.data);
      setSettingsData(settingsRes.data);
      
      setStats({
        orders: ordersRes.data.length,
        submissions: submissionsRes.data.length,
        players: playersRes.data.length,
        products: productsRes.data.length,
        fixtures: fixturesRes.data.length,
        sponsors: sponsorsRes.data.length
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (endpoint, data) => {
    try {
      await axios.post(`/${endpoint}`, data);
      showNotification(`${endpoint} created successfully!`);
      fetchAllData();
      setShowForm(false);
      setFormData({});
    } catch (error) {
      showNotification(`Error creating ${endpoint}: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  const handleUpdate = async (endpoint, id, data) => {
    try {
      await axios.put(`/${endpoint}/${id}`, data);
      showNotification(`${endpoint} updated successfully!`);
      fetchAllData();
      setShowForm(false);
      setEditingItem(null);
      setFormData({});
    } catch (error) {
      showNotification(`Error updating ${endpoint}: ${error.response?.data?.detail || error.message}`, 'error');
    }
  };

  const handleDelete = async (endpoint, id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await axios.delete(`/${endpoint}/${id}`);
        showNotification(`${endpoint} deleted successfully!`);
        fetchAllData();
      } catch (error) {
        showNotification(`Error deleting ${endpoint}: ${error.response?.data?.detail || error.message}`, 'error');
      }
    }
  };

  const handleFormSubmit = (e, endpoint) => {
    e.preventDefault();
    
    // Process formData to handle special fields
    let processedData = { ...formData };
    
    // Convert comma-separated strings to arrays for specific fields
    if (endpoint === 'products') {
      if (processedData.images && typeof processedData.images === 'string') {
        processedData.images = processedData.images
          .split(',')
          .map(img => img.trim())
          .filter(img => img.length > 0);
      }
      if (processedData.sizes && typeof processedData.sizes === 'string') {
        processedData.sizes = processedData.sizes
          .split(',')
          .map(size => size.trim())
          .filter(size => size.length > 0);
      }
    }
    
    if (editingItem) {
      handleUpdate(endpoint, editingItem.id, processedData);
    } else {
      handleCreate(endpoint, processedData);
    }
  };

  // Player Management Functions
  const fetchPlayerDetails = async (playerId) => {
    try {
      const [statsRes, paymentsRes, feedbackRes] = await Promise.all([
        axios.get(`/players/${playerId}/stats`).catch(() => ({ data: null })),
        axios.get(`/players/${playerId}/payments`).catch(() => ({ data: [] })),
        axios.get(`/players/${playerId}/feedback`).catch(() => ({ data: [] }))
      ]);
      
      setPlayerStats(prev => ({ ...prev, [playerId]: statsRes.data }));
      setPlayerPayments(prev => ({ ...prev, [playerId]: paymentsRes.data }));
      setPlayerFeedback(prev => ({ ...prev, [playerId]: feedbackRes.data }));
    } catch (error) {
      console.error('Error fetching player details:', error);
    }
  };

  const updatePlayerStats = async (playerId, statsData) => {
    try {
      await axios.put(`/players/${playerId}/stats`, statsData);
      fetchPlayerDetails(playerId);
      showNotification('Player stats updated successfully', 'success');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Error updating stats', 'error');
    }
  };

  const addPlayerPayment = async (playerId, paymentData) => {
    try {
      await axios.post(`/players/${playerId}/payments`, paymentData);
      fetchPlayerDetails(playerId);
      showNotification('Payment added successfully', 'success');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Error adding payment', 'error');
    }
  };

  const addCoachFeedback = async (playerId, feedbackData) => {
    try {
      await axios.post(`/players/${playerId}/feedback`, feedbackData);
      fetchPlayerDetails(playerId);
      showNotification('Feedback added successfully', 'success');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Error adding feedback', 'error');
    }
  };

  const resetPlayerPassword = async (playerId, newPassword) => {
    try {
      // First get the player to get user_id
      const player = players.find(p => p.id === playerId);
      if (!player) throw new Error('Player not found');
      
      await axios.put(`/auth/reset-password/${player.user_id}`, { password: newPassword });
      showNotification('Password reset successfully', 'success');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Error resetting password', 'error');
    }
  };

  const createPlayerAccount = async (playerId, username, password, email) => {
    try {
      await axios.post('/admin/create-player-account', {
        player_id: playerId,
        username: username,
        password: password,
        email: email
      });
      showNotification('Player account created successfully', 'success');
      // Refresh player data
      fetchAllData();
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Error creating player account', 'error');
    }
  };

  const updatePaymentStatus = async (playerId, paymentId, newStatus) => {
    try {
      await axios.put(`/players/${playerId}/payments/${paymentId}`, { status: newStatus });
      fetchPlayerDetails(playerId);
      showNotification('Payment status updated successfully', 'success');
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Error updating payment status', 'error');
    }
  };

  const checkPlayerHasAccount = (player) => {
    // Check if user_id looks like a UUID (has dashes) vs a name
    return player.user_id && player.user_id.includes('-') && player.user_id.length > 20;
  };

  const openForm = (type, item = null) => {
    setEditingItem(item);
    
    // Process item data for form display
    let processedItem = item ? { ...item } : {};
    
    // Convert arrays to comma-separated strings for form display
    if (type === 'products' && item) {
      if (item.images && Array.isArray(item.images)) {
        processedItem.images = item.images.join(', ');
      }
      if (item.sizes && Array.isArray(item.sizes)) {
        processedItem.sizes = item.sizes.join(', ');
      }
    }
    
    setFormData(processedItem);
    setShowForm(type);
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: TrendingUp },
    { id: 'products', name: 'Products', icon: ShoppingBag },
    { id: 'orders', name: 'Orders', icon: ShoppingBag },
    { id: 'players', name: 'Players', icon: Users },
    { id: 'player-management', name: 'Player Management', icon: Users },
    { id: 'fixtures', name: 'Fixtures', icon: Calendar },
    { id: 'sponsors', name: 'Sponsors', icon: Star },
    { id: 'news', name: 'News', icon: MessageSquare },
    { id: 'submissions', name: 'Join Us', icon: MessageSquare },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  const renderTable = (data, columns, endpoint) => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="flex justify-between items-center p-6 border-b">
        <h3 className="text-lg font-semibold capitalize">{endpoint} Management</h3>
        <button
          onClick={() => openForm(endpoint)}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add {endpoint.slice(0, -1)}
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id}>
                {columns.map((col) => (
                  <td key={col.key}>
                    {col.render ? col.render(item[col.key], item) : item[col.key]}
                  </td>
                ))}
                <td>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openForm(endpoint, item)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(endpoint, item.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderForm = () => {
    if (!showForm) return null;

    const formFields = {
      products: [
        { name: 'name', label: 'Product Name', type: 'text', required: true },
        { name: 'description', label: 'Description', type: 'textarea', required: true },
        { name: 'price', label: 'Price', type: 'number', required: true },
        { name: 'stock', label: 'Stock', type: 'number', required: true },
        { name: 'category', label: 'Category', type: 'text', required: true },
        { name: 'sizes', label: 'Sizes (comma separated)', type: 'text' },
        { name: 'images', label: 'Image URLs (comma separated)', type: 'textarea' }
      ],
      players: [
        { name: 'user_id', label: 'User ID', type: 'text', required: true },
        { name: 'jersey_number', label: 'Jersey Number', type: 'number', required: true },
        { name: 'position', label: 'Position', type: 'select', options: ['Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Staff'], required: true },
        { name: 'team_type', label: 'Team', type: 'select', options: ['Vets', 'Staff', 'Senior'], required: true },
        { name: 'vets_team', label: 'Vets Team', type: 'select', options: ['Crawley Athletic', 'Crawley RVB'] },
        { name: 'nationality', label: 'Nationality', type: 'text', required: true },
        { name: 'bio', label: 'Biography', type: 'textarea' },
        { name: 'photo_url', label: 'Photo URL', type: 'text' }
      ],
      fixtures: [
        { name: 'away_team', label: 'Away Team', type: 'text', required: true },
        { name: 'match_date', label: 'Match Date', type: 'date', required: true },
        { name: 'match_time', label: 'Match Time', type: 'time', required: true },
        { name: 'venue', label: 'Venue', type: 'text', required: true },
        { name: 'competition', label: 'Competition', type: 'text', required: true },
        { name: 'is_home', label: 'Home Match', type: 'checkbox' },
        { name: 'status', label: 'Status', type: 'select', options: ['upcoming', 'completed', 'cancelled'] },
        { name: 'home_score', label: 'Home Score', type: 'number' },
        { name: 'away_score', label: 'Away Score', type: 'number' },
        { name: 'match_report', label: 'Match Report', type: 'textarea' }
      ],
      sponsors: [
        { name: 'name', label: 'Sponsor Name', type: 'text', required: true },
        { name: 'logo_url', label: 'Logo URL', type: 'text', required: true },
        { name: 'website_url', label: 'Website URL', type: 'text' },
        { name: 'category', label: 'Category', type: 'text', required: true },
        { name: 'description', label: 'Description', type: 'textarea' }
      ],
      news: [
        { name: 'title', label: 'Title', type: 'text', required: true },
        { name: 'summary', label: 'Summary', type: 'textarea' },
        { name: 'content', label: 'Content', type: 'textarea', required: true },
        { name: 'image_url', label: 'Image URL', type: 'text' },
        { name: 'is_published', label: 'Published', type: 'checkbox' }
      ]
    };

    const fields = formFields[showForm] || [];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {editingItem ? 'Edit' : 'Add'} {showForm.slice(0, -1)}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={(e) => handleFormSubmit(e, showForm)} className="space-y-4">
              {fields.map((field) => (
                <div key={field.name} className="form-group">
                  <label className="form-label">{field.label}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="form-textarea"
                      required={field.required}
                      rows={3}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="form-input"
                      required={field.required}
                    >
                      <option value="">Select {field.label}</option>
                      {field.options.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : field.type === 'checkbox' ? (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name={field.name}
                        checked={formData[field.name] || false}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-600">Check if {field.label.toLowerCase()}</span>
                    </div>
                  ) : (
                    <input
                      type={field.type}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: field.type === 'number' ? Number(e.target.value) : e.target.value })}
                      className="form-input"
                      required={field.required}
                    />
                  )}
                </div>
              ))}

              <div className="flex space-x-4 pt-4">
                <button type="submit" className="btn btn-primary">
                  <Save className="h-4 w-4 mr-2" />
                  {editingItem ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          <div className="flex items-center">
            {notification.type === 'error' ? (
              <AlertCircle className="h-5 w-5 mr-2" />
            ) : (
              <CheckCircle className="h-5 w-5 mr-2" />
            )}
            {notification.message}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-black text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Admin Control Panel</h1>
              <p className="text-gray-200">Complete Website Management System</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-300">Welcome, {user?.full_name}</p>
              <p className="text-sm text-gray-300">Admin Access</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="admin-nav mb-8">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={activeTab === tab.id ? 'active' : ''}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Dashboard Overview</h2>
            
            {/* Database Initialization Alert */}
            {databaseStatus.needs_initialization && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-4">
                  <div className="text-yellow-600 mr-3">⚠️</div>
                  <h3 className="text-lg font-semibold text-yellow-800">Database Initialization Required</h3>
                </div>
                <p className="text-yellow-700 mb-4">
                  The database appears to be empty. Click below to initialize it with essential data 
                  (admin user, sample products, fixtures, and news).
                </p>
                <div className="text-sm text-yellow-600 mb-4">
                  <p>• Users: {databaseStatus.users_count || 0}</p>
                  <p>• Products: {databaseStatus.products_count || 0}</p>
                  <p>• News: {databaseStatus.news_count || 0}</p>
                  <p>• Fixtures: {databaseStatus.fixtures_count || 0}</p>
                </div>
                <button
                  onClick={initializeDatabase}
                  className="bg-yellow-600 text-white px-6 py-2 rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Initializing...' : 'Initialize Database'}
                </button>
              </div>
            )}
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading statistics...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div 
                  className="card text-center cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  onClick={() => setActiveTab('orders')}
                >
                  <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-black" />
                  <h3 className="text-3xl font-bold text-black">{stats.orders}</h3>
                  <p className="text-gray-600">Total Orders</p>
                  <p className="text-xs text-gray-400 mt-1">Click to view details</p>
                </div>
                
                <div 
                  className="card text-center cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  onClick={() => setActiveTab('players')}
                >
                  <Users className="h-12 w-12 mx-auto mb-4 text-black" />
                  <h3 className="text-3xl font-bold text-black">{stats.players}</h3>
                  <p className="text-gray-600">Registered Players</p>
                  <p className="text-xs text-gray-400 mt-1">Click to view details</p>
                </div>
                
                <div 
                  className="card text-center cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  onClick={() => setActiveTab('products')}
                >
                  <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-black" />
                  <h3 className="text-3xl font-bold text-black">{stats.products}</h3>
                  <p className="text-gray-600">Products</p>
                  <p className="text-xs text-gray-400 mt-1">Click to view details</p>
                </div>
                
                <div 
                  className="card text-center cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  onClick={() => setActiveTab('fixtures')}
                >
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-black" />
                  <h3 className="text-3xl font-bold text-black">{stats.fixtures}</h3>
                  <p className="text-gray-600">Fixtures</p>
                  <p className="text-xs text-gray-400 mt-1">Click to view details</p>
                </div>
                
                <div 
                  className="card text-center cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  onClick={() => setActiveTab('submissions')}
                >
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-black" />
                  <h3 className="text-3xl font-bold text-black">{stats.submissions}</h3>
                  <p className="text-gray-600">Join Us Submissions</p>
                  <p className="text-xs text-gray-400 mt-1">Click to view details</p>
                </div>
                
                <div 
                  className="card text-center cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  onClick={() => setActiveTab('sponsors')}
                >
                  <Star className="h-12 w-12 mx-auto mb-4 text-black" />
                  <h3 className="text-3xl font-bold text-black">{stats.sponsors}</h3>
                  <p className="text-gray-600">Sponsors</p>
                  <p className="text-xs text-gray-400 mt-1">Click to view details</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Products Management */}
        {activeTab === 'products' && renderTable(products, [
          { key: 'name', label: 'Name' },
          { key: 'price', label: 'Price', render: (price) => `£${price.toFixed(2)}` },
          { key: 'stock', label: 'Stock' },
          { key: 'category', label: 'Category' },
        ], 'products')}

        {/* Orders Management */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Orders Management</h3>
              <p className="text-sm text-gray-600 mt-1">Complete order details and customer information</p>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer Details</th>
                    <th>Product</th>
                    <th>Order Details</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <OrderRow key={order.id} order={order} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Players Management */}
        {activeTab === 'players' && renderTable(players, [
          { key: 'jersey_number', label: 'Number' },
          { key: 'position', label: 'Position' },
          { key: 'team_type', label: 'Team' },
          { key: 'vets_team', label: 'Vets Team' },
          { key: 'nationality', label: 'Nationality' },
        ], 'players')}

        {/* Enhanced Player Management */}
        {activeTab === 'player-management' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Advanced Player Management</h3>
              
              {/* Player Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Player to Manage:
                </label>
                <select
                  value={selectedPlayer || ''}
                  onChange={(e) => {
                    const playerId = e.target.value;
                    setSelectedPlayer(playerId);
                    if (playerId) fetchPlayerDetails(playerId);
                  }}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-black focus:border-black"
                >
                  <option value="">Choose a player...</option>
                  {players.map(player => {
                    const hasAccount = checkPlayerHasAccount(player);
                    const playerName = player.full_name || player.user_id || `Player #${player.jersey_number}`;
                    return (
                      <option key={player.id} value={player.id}>
                        {playerName} (#{player.jersey_number})
                        {!hasAccount ? ' - ⚠️ NO LOGIN ACCOUNT' : ' - ✅ HAS ACCOUNT'}
                      </option>
                    );
                  })}
                </select>
              </div>

              {selectedPlayer && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  
                  {/* Password & Account Management */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Account Management</h4>
                    {selectedPlayer && (
                      (() => {
                        const player = players.find(p => p.id === selectedPlayer);
                        const hasAccount = checkPlayerHasAccount(player);
                        
                        if (!hasAccount) {
                          return (
                            <div className="space-y-3">
                              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800 mb-2">
                                  <strong>No Login Account:</strong> This player cannot access their stats. Create a login account below.
                                </p>
                              </div>
                              <input
                                type="text"
                                placeholder="Username"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                id="new-username"
                              />
                              <input
                                type="email"
                                placeholder="Email"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                id="new-email"
                              />
                              <input
                                type="password"
                                placeholder="Password"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                id="new-password"
                              />
                              <button
                                onClick={() => {
                                  const username = document.getElementById('new-username').value;
                                  const email = document.getElementById('new-email').value;
                                  const password = document.getElementById('new-password').value;
                                  if (username && email && password) {
                                    createPlayerAccount(selectedPlayer, username, password, email);
                                    document.getElementById('new-username').value = '';
                                    document.getElementById('new-email').value = '';
                                    document.getElementById('new-password').value = '';
                                  }
                                }}
                                className="w-full bg-green-600 text-white p-2 rounded-md hover:bg-green-700"
                              >
                                Create Login Account
                              </button>
                            </div>
                          );
                        } else {
                          return (
                            <div className="space-y-3">
                              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-800">✅ Player has login account</p>
                              </div>
                              <input
                                type="password"
                                placeholder="New password"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    resetPlayerPassword(selectedPlayer, e.target.value);
                                    e.target.value = '';
                                  }
                                }}
                              />
                              <p className="text-xs text-gray-500">Press Enter to set new password</p>
                            </div>
                          );
                        }
                      })()
                    )}
                  </div>

                  {/* Stats Management */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Player Statistics</h4>
                    <div className="space-y-2">
                      {playerStats[selectedPlayer] ? (
                        <div key={selectedPlayer} className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Goals:</span>
                            <input
                              type="number"
                              key={`goals-${selectedPlayer}`}
                              defaultValue={playerStats[selectedPlayer]?.goals || 0}
                              onBlur={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                if (newValue !== (playerStats[selectedPlayer]?.goals || 0)) {
                                  updatePlayerStats(selectedPlayer, {
                                    ...playerStats[selectedPlayer],
                                    goals: newValue
                                  });
                                }
                              }}
                              className="w-16 p-1 border rounded text-center"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Games Played:</span>
                            <input
                              type="number"
                              key={`matches-${selectedPlayer}`}
                              defaultValue={playerStats[selectedPlayer]?.matches_played || 0}
                              onBlur={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                if (newValue !== (playerStats[selectedPlayer]?.matches_played || 0)) {
                                  updatePlayerStats(selectedPlayer, {
                                    ...playerStats[selectedPlayer],
                                    matches_played: newValue
                                  });
                                }
                              }}
                              className="w-16 p-1 border rounded text-center"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Yellow Cards:</span>
                            <input
                              type="number"
                              key={`yellow-${selectedPlayer}`}
                              defaultValue={playerStats[selectedPlayer]?.yellow_cards || 0}
                              onBlur={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                if (newValue !== (playerStats[selectedPlayer]?.yellow_cards || 0)) {
                                  updatePlayerStats(selectedPlayer, {
                                    ...playerStats[selectedPlayer],
                                    yellow_cards: newValue
                                  });
                                }
                              }}
                              className="w-16 p-1 border rounded text-center"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Red Cards:</span>
                            <input
                              type="number"
                              key={`red-${selectedPlayer}`}
                              defaultValue={playerStats[selectedPlayer]?.red_cards || 0}
                              onBlur={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                if (newValue !== (playerStats[selectedPlayer]?.red_cards || 0)) {
                                  updatePlayerStats(selectedPlayer, {
                                    ...playerStats[selectedPlayer],
                                    red_cards: newValue
                                  });
                                }
                              }}
                              className="w-16 p-1 border rounded text-center"
                            />
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Minutes:</span>
                            <input
                              type="number"
                              key={`minutes-${selectedPlayer}`}
                              defaultValue={playerStats[selectedPlayer]?.minutes_played || 0}
                              onBlur={(e) => {
                                const newValue = parseInt(e.target.value) || 0;
                                if (newValue !== (playerStats[selectedPlayer]?.minutes_played || 0)) {
                                  updatePlayerStats(selectedPlayer, {
                                    ...playerStats[selectedPlayer],
                                    minutes_played: newValue
                                  });
                                }
                              }}
                              className="w-20 p-1 border rounded text-center"
                            />
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No stats available</p>
                      )}
                    </div>
                  </div>

                  {/* Payment Management */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Payment Status</h4>
                    {playerPayments[selectedPlayer] && playerPayments[selectedPlayer].length > 0 ? (
                      <div className="space-y-2">
                        {playerPayments[selectedPlayer].map((payment, index) => (
                          <div key={index} className="flex justify-between items-center text-sm">
                            <span>{payment.description}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium">£{payment.amount}</span>
                              <button
                                onClick={() => {
                                  const newStatus = payment.status === 'paid' ? 'outstanding' : 'paid';
                                  updatePaymentStatus(selectedPlayer, payment.id, newStatus);
                                }}
                                className={`px-2 py-1 rounded text-xs cursor-pointer hover:opacity-75 ${
                                  payment.status === 'paid' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {payment.status === 'paid' ? '✅ PAID' : '⚠️ OUTSTANDING'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No payment records</p>
                    )}
                    
                    {/* Add New Payment */}
                    <div className="mt-3 pt-3 border-t">
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Description"
                          className="w-full p-2 border rounded text-sm"
                          id="payment-desc"
                        />
                        <div className="flex space-x-2">
                          <input
                            type="number"
                            placeholder="Amount"
                            className="flex-1 p-2 border rounded text-sm"
                            id="payment-amount"
                          />
                          <select className="flex-1 p-2 border rounded text-sm" id="payment-status">
                            <option value="outstanding">Outstanding</option>
                            <option value="paid">Paid</option>
                          </select>
                        </div>
                        <button
                          onClick={() => {
                            const desc = document.getElementById('payment-desc').value;
                            const amount = document.getElementById('payment-amount').value;
                            const status = document.getElementById('payment-status').value;
                            if (desc && amount) {
                              addPlayerPayment(selectedPlayer, {
                                description: desc,
                                amount: parseFloat(amount),
                                status: status,
                                due_date: new Date().toISOString()
                              });
                              document.getElementById('payment-desc').value = '';
                              document.getElementById('payment-amount').value = '';
                            }
                          }}
                          className="w-full bg-black text-white p-2 rounded text-sm hover:bg-gray-800"
                        >
                          Add Payment
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedPlayer && (
                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Manager Notes</h4>
                  
                  {/* Recent Feedback */}
                  {playerFeedback[selectedPlayer] && playerFeedback[selectedPlayer].length > 0 && (
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Recent Notes:</h5>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {playerFeedback[selectedPlayer].slice(0, 3).map((feedback, index) => (
                          <div key={index} className="text-sm p-2 bg-white rounded border">
                            <div className="font-medium">{feedback.category}</div>
                            <div className="text-gray-600">{feedback.feedback}</div>
                            <div className="text-xs text-gray-500">{new Date(feedback.date).toLocaleDateString()}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add New Manager Note */}
                  <div className="space-y-3">
                    <select className="w-full p-2 border rounded" id="feedback-category">
                      <option value="Performance">Performance</option>
                      <option value="Training">Training</option>
                      <option value="Discipline">Discipline</option>
                      <option value="General">General</option>
                    </select>
                    <textarea
                      placeholder="Add manager note..."
                      className="w-full p-3 border border-gray-300 rounded-md resize-none"
                      rows="3"
                      id="feedback-text"
                    ></textarea>
                    <div className="flex space-x-2">
                      <select className="p-2 border rounded" id="feedback-rating">
                        <option value="">No Rating</option>
                        <option value="1">1 Star</option>
                        <option value="2">2 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="5">5 Stars</option>
                      </select>
                      <button
                        onClick={() => {
                          const category = document.getElementById('feedback-category').value;
                          const feedback = document.getElementById('feedback-text').value;
                          const rating = document.getElementById('feedback-rating').value;
                          if (feedback.trim()) {
                            addCoachFeedback(selectedPlayer, {
                              coach_name: 'Manager',
                              category: category,
                              feedback: feedback,
                              rating: rating ? parseInt(rating) : null,
                              date: new Date().toISOString()
                            });
                            document.getElementById('feedback-text').value = '';
                          }
                        }}
                        className="flex-1 bg-black text-white p-2 rounded hover:bg-gray-800"
                      >
                        Add Manager Note
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Fixtures Management */}
        {activeTab === 'fixtures' && renderTable(fixtures, [
          { key: 'away_team', label: 'Opponent' },
          { key: 'match_date', label: 'Date' },
          { key: 'match_time', label: 'Time' },
          { key: 'venue', label: 'Venue' },
          { key: 'status', label: 'Status' },
        ], 'fixtures')}

        {/* Sponsors Management */}
        {activeTab === 'sponsors' && renderTable(sponsors, [
          { key: 'name', label: 'Name' },
          { key: 'category', label: 'Category' },
          { key: 'website_url', label: 'Website', render: (url) => url ? 'Yes' : 'No' },
        ], 'sponsors')}

        {/* News Management */}
        {activeTab === 'news' && renderTable(news, [
          { key: 'title', label: 'Title' },
          { key: 'is_published', label: 'Published', render: (published) => published ? 'Yes' : 'No' },
          { key: 'created_at', label: 'Date', render: (date) => new Date(date).toLocaleDateString() },
        ], 'news')}

        {/* Join Us Submissions */}
        {activeTab === 'submissions' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Join Us Submissions</h3>
              <p className="text-sm text-gray-600 mt-1">Complete submission details and messages</p>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Contact Details</th>
                    <th>Interest</th>
                    <th>Message</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <SubmissionRow 
                      key={submission.id} 
                      submission={submission} 
                      onDelete={(id) => {
                        setSubmissions(submissions.filter(s => s.id !== id));
                        setStats(prev => ({ ...prev, submissions: prev.submissions - 1 }));
                        setNotification({ type: 'success', message: 'Submission deleted successfully' });
                        setTimeout(() => setNotification(null), 3000);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Settings */}
        {activeTab === 'settings' && (
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Club Settings</h3>
            <div className="space-y-6">
              <div>
                <label className="form-label">WhatsApp Number</label>
                <input
                  type="tel"
                  className="form-input"
                  defaultValue={settings.whatsapp_number}
                  placeholder="+44123456789"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">General Email</label>
                  <input
                    type="email"
                    className="form-input"
                    defaultValue={settings.club_email}
                  />
                </div>
                <div>
                  <label className="form-label">Sales Email</label>
                  <input
                    type="email"
                    className="form-input"
                    defaultValue={settings.sales_email}
                  />
                </div>
              </div>
              
              <div>
                <label className="form-label">Club Address</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  defaultValue={settings.club_address}
                />
              </div>
              
              <button className="btn btn-primary">
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {renderForm()}
    </div>
  );
};

// Enhanced OrderRow component to show all order details
const OrderRow = ({ order }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <>
      <tr className={expanded ? 'bg-gray-50' : ''}>
        <td className="font-mono text-sm">{order.id.slice(0, 8)}</td>
        <td>
          <div className="text-sm">
            <div className="font-medium">{order.customer_name}</div>
            <div className="text-gray-500">{order.customer_email}</div>
            <div className="text-gray-500">{order.customer_phone}</div>
          </div>
        </td>
        <td>
          <div className="text-sm">
            <div className="font-medium">{order.product_name}</div>
            {order.size && <div className="text-gray-500">Size: {order.size}</div>}
            <div className="text-gray-500">Qty: {order.quantity || 1}</div>
          </div>
        </td>
        <td>
          <div className="text-sm">
            <div>Qty: {order.quantity || 1}</div>
            {order.size && <div>Size: {order.size}</div>}
            {order.customer_address && (
              <div className="text-gray-500 truncate max-w-xs" title={order.customer_address}>
                📍 {order.customer_address.length > 30 ? order.customer_address.substring(0, 30) + '...' : order.customer_address}
              </div>
            )}
          </div>
        </td>
        <td className="font-semibold">£{order.total_amount.toFixed(2)}</td>
        <td>
          <span className={`status-badge status-${order.status}`}>
            {order.status}
          </span>
        </td>
        <td className="text-sm">{new Date(order.created_at).toLocaleDateString()}</td>
        <td>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            {expanded ? 'Hide' : 'Details'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan="8" className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Customer Information</h4>
                <div className="space-y-1">
                  <div><span className="font-medium">Name:</span> {order.customer_name}</div>
                  <div><span className="font-medium">Email:</span> {order.customer_email}</div>
                  <div><span className="font-medium">Phone:</span> {order.customer_phone}</div>
                  {order.customer_address && (
                    <div><span className="font-medium">Address:</span> {order.customer_address}</div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Order Information</h4>
                <div className="space-y-1">
                  <div><span className="font-medium">Product:</span> {order.product_name}</div>
                  <div><span className="font-medium">Quantity:</span> {order.quantity || 1}</div>
                  {order.size && <div><span className="font-medium">Size:</span> {order.size}</div>}
                  <div><span className="font-medium">Total:</span> £{order.total_amount.toFixed(2)}</div>
                  <div><span className="font-medium">Status:</span> <span className={`status-badge status-${order.status}`}>{order.status}</span></div>
                  <div><span className="font-medium">Order Date:</span> {new Date(order.created_at).toLocaleString()}</div>
                </div>
              </div>
              {order.notes && (
                <div className="md:col-span-2">
                  <h4 className="font-semibold mb-2">Customer Notes</h4>
                  <div className="bg-white p-3 rounded border">
                    {order.notes}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Enhanced SubmissionRow component to show all submission details
const SubmissionRow = ({ submission, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete the submission from ${submission.full_name}?`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`/join-us/${submission.id}`);
      onDelete(submission.id);
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Failed to delete submission');
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <>
      <tr className={expanded ? 'bg-gray-50' : ''}>
        <td>
          <div className="text-sm">
            <div className="font-medium">{submission.full_name}</div>
            <div className="text-gray-500">{submission.email}</div>
            <div className="text-gray-500">{submission.phone_number}</div>
          </div>
        </td>
        <td>
          <div className="flex flex-wrap gap-1">
            {submission.interest_types.map((interest, index) => (
              <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {interest}
              </span>
            ))}
          </div>
        </td>
        <td>
          <div className="text-sm text-gray-600 max-w-xs truncate" title={submission.message}>
            {submission.message.length > 50 ? submission.message.substring(0, 50) + '...' : submission.message}
          </div>
        </td>
        <td className="text-sm">{new Date(submission.created_at).toLocaleDateString()}</td>
        <td>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
              title="Delete submission"
            >
              {deleting ? '...' : <Trash2 className="h-4 w-4" />}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan="5" className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Contact Information</h4>
                <div className="space-y-1">
                  <div><span className="font-medium">Full Name:</span> {submission.full_name}</div>
                  <div><span className="font-medium">Email:</span> {submission.email}</div>
                  <div><span className="font-medium">Phone:</span> {submission.phone_number}</div>
                  <div><span className="font-medium">Submitted:</span> {new Date(submission.created_at).toLocaleString()}</div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Interest Areas</h4>
                <div className="flex flex-wrap gap-2">
                  {submission.interest_types.map((interest, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <h4 className="font-semibold mb-2">Complete Message</h4>
                <div className="bg-white p-4 rounded border">
                  <p className="whitespace-pre-wrap">{submission.message}</p>
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleting ? 'Deleting...' : 'Delete Submission'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default AdminPanel;